import {
  IPlatformService,
  Platform,
  TVKeyMap,
  DeviceInfo,
  ScreenOrientation,
  OrientationChangeCallback,
} from '../types';
import { DESKTOP_KEYS } from '../keys';
import { generateUUID } from '../../../utils/uuid';

/**
 * DesktopPlatformService — wrapper Tauri v2 (Windows / Linux).
 *
 * O player desktop é um WebView fullscreen dentro de um wrapper Tauri fino
 * (WebView2 no Windows, WebKitGTK no Linux). Do ponto de vista do React, é "um
 * browser num device dedicado": as APIs DOM (orientation, fullscreen, wake
 * lock) funcionam; o que é NATIVO (device id, reboot, screenshot, OTA install)
 * vai por IPC pro Rust.
 *
 * Um único service serve os 2 OSes — o comportamento é idêntico; só o rótulo
 * (`platform`) e alguns detalhes de `getDeviceInfo` mudam. macOS está fora do
 * V1 (plano §11). Ver
 * `efflux-server/docs/roadmaps/FEATURE-DESKTOP-PLAYER-TAURI.MD`.
 *
 * ⚠️ Fase atual: o IPC Rust ainda NÃO existe (POC F1 carrega a URL remota).
 * Todos os métodos nativos degradam com elegância (fallback ou 'unsupported')
 * até a Fase 3/4 plugarem os comandos `invoke(...)`.
 */

/** Forma mínima do WakeLockSentinel (não depende do lib.dom target). */
interface WakeLockSentinelLike {
  release(): Promise<void>;
  addEventListener?: (type: 'release', listener: () => void) => void;
}

/** Ponte opcional pro Tauri v2 (só existe quando roda dentro do wrapper). */
type TauriInvoke = (
  cmd: string,
  args?: Record<string, unknown>,
) => Promise<unknown>;
type WindowWithTauri = Window & {
  __TAURI__?: { core?: { invoke?: TauriInvoke } };
};

export type DesktopOs = 'windows' | 'linux';

export class DesktopPlatformService implements IPlatformService {
  readonly platform: Platform;
  readonly keys: TVKeyMap = DESKTOP_KEYS;

  private readonly os: DesktopOs;
  private isInitialized = false;
  private orientationCallbacks: Set<OrientationChangeCallback> = new Set();
  private currentOrientation: ScreenOrientation = 'landscape';
  private wakeLockSentinel: WakeLockSentinelLike | null = null;
  private wantsWakeLock = false;
  private wakeLockVisibilityBound = false;

  constructor(os: DesktopOs = 'windows') {
    this.os = os;
    this.platform = os;
  }

  // ---- Ponte Tauri (native IPC) --------------------------------------------

  /** Retorna a função invoke do Tauri, ou null quando fora do wrapper. */
  private tauriInvoke(): TauriInvoke | null {
    const w = window as WindowWithTauri;
    return w.__TAURI__?.core?.invoke ?? null;
  }

  /**
   * Chama um comando nativo do wrapper. Quando o IPC não existe (POC/URL
   * remota) devolve null — o caller decide o fallback.
   */
  private async invokeNative<T>(
    cmd: string,
    args?: Record<string, unknown>,
  ): Promise<T | null> {
    const invoke = this.tauriInvoke();
    if (!invoke) return null;
    try {
      return (await invoke(cmd, args)) as T;
    } catch (e) {
      console.warn(`[DesktopPlatformService] invoke('${cmd}') falhou:`, e);
      return null;
    }
  }

  private unsupported(message: string): Error {
    const err = new Error(message);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (err as any).code = 'unsupported';
    return err;
  }

  // ---- Lifecycle -----------------------------------------------------------

  init(): void {
    if (this.isInitialized) return;
    this.isInitialized = true;
    console.log(`[DesktopPlatformService] Initializing (os=${this.os})`);
    this.currentOrientation = this.detectOrientation();
    if (screen.orientation) {
      screen.orientation.addEventListener(
        'change',
        this.handleOrientationChange,
      );
    } else {
      window.addEventListener('resize', this.handleOrientationChange);
    }
  }

  exit(): void {
    // Fecha o app via wrapper. Sem IPC (POC), no-op — o wrapper controla a saída.
    void this.invokeNative('app_exit');
  }

  onHide(): void {
    console.log('[DesktopPlatformService] Window hidden');
  }

  onResume(): void {
    console.log('[DesktopPlatformService] Window resumed');
  }

  // ---- Capabilities --------------------------------------------------------

  /** É um device dedicado (kiosk), não um browser casual. */
  isNativeEnvironment(): boolean {
    return true;
  }

  /** O wrapper pode encerrar o processo (via IPC, Fase 3). */
  canExit(): boolean {
    return true;
  }

  hasColorButtons(): boolean {
    return false;
  }

  hasMediaKeys(): boolean {
    return false;
  }

  /** Não é TV: tem toque ou mouse, e não sofre o limite de 1 decoder das TVs. */
  isTV(): boolean {
    return false;
  }

  /**
   * Restart remoto (PLAYER_RESTART_PLAN).
   * 'refresh' = reload do WebView (sempre). 'reboot_device' = reinicia o PC
   * inteiro via wrapper (Rust: `shutdown /r` no Win, `systemctl reboot` no
   * Linux, `shutdown -r` no macOS) — indisponível até a Fase 3 do IPC.
   */
  async restart(mode: 'refresh' | 'reboot_device'): Promise<void> {
    if (mode === 'reboot_device') {
      const ok = await this.invokeNative<boolean>('reboot_device');
      if (ok === null) {
        throw this.unsupported(
          'reboot_device indisponível — IPC do wrapper ainda não implementado (Fase 3)',
        );
      }
      return;
    }
    console.log('[DesktopPlatformService] Restart refresh — reload do WebView');
    window.location.reload();
  }

  // ---- Device identification -----------------------------------------------

  /**
   * ID de hardware estável. Preferência: machine id nativo via wrapper
   * (Rust: Machine GUID no Win / /etc/machine-id no Linux / IOPlatformUUID no
   * macOS). Fallback (POC): UUID persistido em localStorage, prefixado pelo OS
   * — estável entre reinícios do app, sobrevive na mesma origem.
   */
  async getHardwareId(): Promise<string> {
    const nativeId = await this.invokeNative<string>('get_machine_id');
    if (nativeId) {
      return nativeId;
    }
    const key = 'efflux_desktop_hardware_id';
    let id = localStorage.getItem(key);
    if (!id) {
      const prefix = this.os.toUpperCase();
      id = `${prefix}-${generateUUID().substring(0, 8).toUpperCase()}`;
      localStorage.setItem(key, id);
    }
    console.log('[DesktopPlatformService] Using device ID:', id);
    return id;
  }

  /**
   * N/A — o wrapper Tauri não expõe memória do sistema ao webview hoje. Daria
   * pra resolver com um comando nativo do Tauri; enquanto não há, `null` diz
   * "não sei medir" em vez de fingir um número.
   */
  async getSystemMemoryMb(): Promise<{
    freeMb: number;
    totalMb: number | null;
  } | null> {
    return null;
  }

  async getDeviceInfo(): Promise<DeviceInfo> {
    const manufacturerByOs: Record<DesktopOs, string> = {
      windows: 'Windows PC',
      linux: 'Linux PC',
    };
    const info: DeviceInfo = {
      platform: this.platform,
      screenResolution: `${screen.width}x${screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      orientation: this.getOrientation(),
      manufacturer: manufacturerByOs[this.os],
      model: `Efflux Desktop (${this.os})`,
      platformVersion: navigator.platform || 'Unknown',
    };

    // Detalhes finos (OS build, edition, arch) via wrapper — Fase 3.
    const nativeInfo =
      await this.invokeNative<Partial<DeviceInfo>>('get_device_info');
    if (nativeInfo) {
      Object.assign(info, nativeInfo, { platform: this.platform });
    }
    return info;
  }

  // ---- Orientation ---------------------------------------------------------

  private handleOrientationChange = (): void => {
    const next = this.detectOrientation();
    if (next !== this.currentOrientation) {
      this.currentOrientation = next;
      this.orientationCallbacks.forEach((cb) => {
        try {
          cb(next);
        } catch (e) {
          console.error('[DesktopPlatformService] orientation cb error:', e);
        }
      });
    }
  };

  private detectOrientation(): ScreenOrientation {
    if (screen.orientation?.type) {
      return screen.orientation.type.includes('portrait')
        ? 'portrait'
        : 'landscape';
    }
    return window.innerWidth > window.innerHeight ? 'landscape' : 'portrait';
  }

  supportsOrientationChange(): boolean {
    return true;
  }

  getOrientation(): ScreenOrientation {
    this.currentOrientation = this.detectOrientation();
    return this.currentOrientation;
  }

  onOrientationChange(callback: OrientationChangeCallback): () => void {
    this.orientationCallbacks.add(callback);
    return () => this.orientationCallbacks.delete(callback);
  }

  // ---- Hardware brightness -------------------------------------------------

  /** Brilho de hardware via wrapper (WMI/xrandr/DDC); null quando indisponível. */
  async getHardwareBrightness(): Promise<number | null> {
    return this.invokeNative<number>('get_brightness');
  }

  async setHardwareBrightness(level: number): Promise<void> {
    const ok = await this.invokeNative<boolean>('set_brightness', { level });
    if (ok === null) {
      throw this.unsupported(
        'Brilho de hardware indisponível — usar overlay CSS (BrightnessService)',
      );
    }
  }

  // ---- OTA -----------------------------------------------------------------

  /**
   * OTA do wrapper via Tauri updater (Fase 4). O bundle JS (React) atualiza
   * pelo caminho embedded (EffluxBundleUpdater) — ver plano §3.5.
   */
  async installUpdate(bundlePath: string): Promise<void> {
    const ok = await this.invokeNative<boolean>('install_update', {
      bundlePath,
    });
    if (ok === null) {
      throw this.unsupported(
        'OTA install indisponível — Tauri updater entra na Fase 4',
      );
    }
  }

  restartApp(): void {
    // Relança o processo via wrapper; fallback = reload do WebView.
    this.invokeNative<boolean>('relaunch').then((ok) => {
      if (ok === null) window.location.reload();
    });
  }

  getTempDirectory(): string {
    // Quem atualiza o desktop é o wrapper (tauri-plugin-updater baixa, valida a
    // assinatura minisign e instala fora do WebView) — o OtaService do JS nunca
    // baixa nada aqui, então este path não chega a ser usado pra update. Fica no
    // mesmo default do Browser/Tizen-dev/WebOS-dev e do fallback do OtaService.
    return '/tmp';
  }

  // ---- Fullscreen ----------------------------------------------------------
  // A janela do wrapper já é fullscreen borderless (kiosk). O app NÃO deve
  // togglar fullscreen do browser por dentro — semântica de TV.

  supportsFullscreen(): boolean {
    return false;
  }

  isFullscreen(): boolean {
    return true;
  }

  async enterFullscreen(): Promise<void> {
    // no-op — a janela nativa já é fullscreen.
  }

  async exitFullscreen(): Promise<void> {
    // no-op — kiosk não sai de fullscreen pelo app.
  }

  onFullscreenChange(): () => void {
    // Fullscreen é fixo no kiosk — nunca dispara.
    return () => {};
  }

  // ---- Screen Wake Lock ----------------------------------------------------
  // WebView2/WebKitGTK expõem navigator.wakeLock. O wrapper (Fase 2) também
  // inibe o sleep nativamente; aqui usamos a API DOM como reforço/no POC.

  supportsWakeLock(): boolean {
    return typeof navigator !== 'undefined' && 'wakeLock' in navigator;
  }

  async requestScreenWakeLock(): Promise<void> {
    this.wantsWakeLock = true;
    this.bindWakeLockVisibility();
    await this.acquireWakeLock();
  }

  async releaseScreenWakeLock(): Promise<void> {
    this.wantsWakeLock = false;
    if (this.wakeLockSentinel) {
      try {
        await this.wakeLockSentinel.release();
      } catch (e) {
        console.warn('[DesktopPlatformService] wakeLock release failed:', e);
      }
      this.wakeLockSentinel = null;
    }
  }

  private acquireWakeLock = async (): Promise<void> => {
    if (!this.wantsWakeLock || !this.supportsWakeLock()) return;
    if (this.wakeLockSentinel) return;
    try {
      const nav = navigator as Navigator & {
        wakeLock?: {
          request: (type: 'screen') => Promise<WakeLockSentinelLike>;
        };
      };
      const sentinel = await nav.wakeLock!.request('screen');
      this.wakeLockSentinel = sentinel;
      sentinel.addEventListener?.('release', () => {
        this.wakeLockSentinel = null;
      });
    } catch (e) {
      console.warn('[DesktopPlatformService] wakeLock request failed:', e);
    }
  };

  private bindWakeLockVisibility(): void {
    if (this.wakeLockVisibilityBound) return;
    this.wakeLockVisibilityBound = true;
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        void this.acquireWakeLock();
      }
    });
  }
}
